import { signal } from '@angular/core';

import { deepEqual } from '@core/utils-legacy';

import type { Select2Item } from '../../types';
import { ItemSelector } from '../../types';

export class MultiselectItemSelector<Item extends Select2Item> extends ItemSelector<Item> {
  public selected = signal<Item[]>([], { equal: deepEqual });

  private preselected: Item[] | null = [];
  private items: Item[];
  private formControlValue: Array<Item['value']>;

  public select(item: Item | Item[]) {
    const items = Array.isArray(item) ? item : [item];
    items.forEach(item => {
      const isSelected = this.isSelected(item);
      if (isSelected) {
        this.remove(item);
      } else {
        this.selected.update(selected => [...selected, item]);
      }
    });
    this.selectionChanged$.next();
  }

  public clear() {
    this.selected.set([]);
    this.selectionChanged$.next();
  }

  public isSelected(item: Item) {
    return !!this.selected().find(selectedItem => this.compareItemsFn(selectedItem, item));
  }

  public remove(item: Item) {
    this.selected.update(selected =>
      selected.filter(selectedItem => !this.compareItemsFn(selectedItem, item)),
    );
    this.selectionChanged$.next();
  }

  public getValue(): Array<Item['value']> {
    return this.selected().map(selectedItem => selectedItem.value);
  }

  public setItems(items: Item[]) {
    this.items = items;
    this.updateSelection();
  }

  public setHiddenItems(items: Item[] | null) {
    this.preselected = items;
    this.updateSelection();
  }

  public writeValue(values: Array<Item['value']>) {
    this.formControlValue = values;
    this.updateSelection();
  }

  public updateSelection() {
    if (!this.formControlValue || !this.formControlValue.length) {
      this.selected.set([]);
    } else {
      const allItems = [...(this.preselected ?? []), ...(this.items ?? [])];
      const items = <Item[]>(
        this.formControlValue
          .map(value => allItems.find(item => this.compareItemsFn(item, { value })))
          .filter(Boolean)
      );
      this.selected.set(items);
    }
  }

  public setCompareItemsFn(fn: (a: Item, b: { value: Item['value'] } | Item) => boolean) {
    this.compareItemsFn = fn;
    this.updateSelection();
  }
}
